import { useEffect, useState } from "react";
import { useQueryClient, type QueryClient } from "@tanstack/react-query";
import { useLocation, useNavigate } from "react-router-dom";
import { supabase } from "@/integrations/supabase/client";
import { isPreBeta } from "@/config/launchPeriods";
import { toast } from "sonner";
import { useAuthReady } from "@/hooks/useAuthReady";
import { useFirstProjectSeed } from "@/hooks/useFirstProjectSeed";
import { useIdentityGuard } from "@/hooks/useIdentityGuard";
import { OnboardingOrchestrator } from "@/components/onboarding/OnboardingOrchestrator";
import { GanttSkeleton } from "@/components/loading";
import { getIsArchitect, clearIsArchitectCache } from "@/utils/isArchitectCache";
import { LearnerEntryGate } from "@/components/courseLab/LearnerEntryGate";
import { courseReturnPath } from "@/lib/courseLab/learnerEntry";
import { isSafeRedirectPath } from "@/lib/auth/postAuthRedirect";

// Ownership survives guard unmounts during sign-out/landing/sign-in. This is
// cache hygiene only; every read remains subject to the existing RLS policy.
const cohortCacheOwners = new WeakMap<QueryClient, string>();
const isCohortQuery = (query: { queryKey: readonly unknown[] }) => {
  const key = query.queryKey[0];
  return typeof key === "string" && (key === "course-cohorts" || key.startsWith("course-cohort-"));
};

/**
 * `courseContext` is set by the ROUTE TABLE (App.tsx) for the Course Lab
 * learner surfaces only. It is presentation-only: it confers no access (RLS and
 * the cohort RPCs remain the authority) and it never relaxes the sign-in
 * requirement. It only suppresses general Workitect onboarding — the intro
 * slideshow, the /welcome workspace checklist redirect and sample-project
 * seeding — so a cohort-only learner is never diverted into workspace setup.
 */
export const AuthGuard = ({
  children,
  courseContext = false,
}: {
  children: React.ReactNode;
  courseContext?: boolean;
}) => {
  const navigate = useNavigate();
  const location = useLocation();
  const queryClient = useQueryClient();
  const { user, isReady, isLinking } = useAuthReady();
  const [checked, setChecked] = useState(false);
  const [cacheSubject, setCacheSubject] = useState<string | null>(null);

  // Auto-seed "My First Project" sample for signed-in users (idempotent) —
  // never on a Course Lab learner surface.
  useFirstProjectSeed({ enabled: !courseContext });

  // Do not mount protected children until old-identity queries are cancelled
  // and removed, including when this guard was unmounted between accounts.
  useEffect(() => {
    let current = true;
    setCacheSubject(null);
    if (!isReady || isLinking || !user) return;
    const id = user.id;
    if (cohortCacheOwners.get(queryClient) === id) {
      setCacheSubject(id);
      return;
    }
    void (async () => {
      await queryClient.cancelQueries({ predicate: isCohortQuery });
      if (!current) return;
      queryClient.removeQueries({ predicate: isCohortQuery });
      cohortCacheOwners.set(queryClient, id);
      setCacheSubject(id);
    })();
    return () => { current = false; };
  }, [isReady, isLinking, user?.id, queryClient]);

  // Enforce one-identity-per-email + sync OAuth avatars
  useIdentityGuard();

  // NOTE: the former `auto-assign-architect` bootstrap call has been removed.
  // Global platform authority is bound to one immutable auth subject in
  // `public.platform_owner_registry` and can never be conferred at runtime —
  // including in the zero-owner state. See
  // docs/migrations/platform-owner-authority-runbook.md.


  // Post-auth redirect to /welcome is now owned by <OnboardingOrchestrator>
  // (mounted below). It shows the intro slideshow first, then routes to
  // /welcome only if the 3-step checklist isn't finished — so new users
  // don't get bounced to the checklist before seeing the intro.

  // Pre-beta access check — runs only after auth is fully ready
  useEffect(() => {
    if (!isReady) return;

    // During linking, pause all redirects
    if (isLinking) {
      setChecked(true);
      return;
    }

    if (!user) {
      // Course surfaces hand their own same-origin path to the existing
      // landing sign-in (?redirect=) so a returning learner comes back to the
      // course, not the default app entry. Token-free by construction.
      const back = courseContext ? courseReturnPath(location.pathname, location.search) : null;
      navigate(
        back && isSafeRedirectPath(back) ? `/?redirect=${encodeURIComponent(back)}` : "/",
        { replace: true },
      );
      setChecked(true);
      return;
    }

    // Check pre-beta access
    const checkAccess = async () => {
      if (!isPreBeta()) {
        setChecked(true);
        return;
      }

      // Use is_architect RPC (linked-account aware) — consistent with useUserRole.
      // Deduped via getIsArchitect so InstanceContext / useUserRole reuse the
      // same result during the post-login burst.
      const isArch = await getIsArchitect(user.id).catch(() => false);

      if (!isArch) {
        clearIsArchitectCache();
        toast.info("The app is not yet available. We'll notify you when beta begins.");
        await supabase.auth.signOut();
        navigate("/", { replace: true });
      }

      setChecked(true);
    };

    checkAccess();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isReady, isLinking, user, navigate]);

  if (!isReady || !user || isLinking || cacheSubject !== user.id || (isPreBeta() && !checked)) {
    return <GanttSkeleton className="h-screen" />;
  }

  if (courseContext) return <>{children}</>;

  // Keep one component shape across /app <-> /app/task/:id. Switching the
  // wrapper between LearnerEntryGate and a Fragment remounted Index, reset its
  // selected task, and restarted every app-load request. The gate itself is
  // active only on the bare entry route, so deep links remain unintercepted.
  return (
    <LearnerEntryGate userId={user.id} active={location.pathname === "/app"}>
      <OnboardingOrchestrator />
      {children}
    </LearnerEntryGate>
  );
};
